import { Form, FormProps } from 'antd'
import { useProjectSettingsContext } from '../../../../../../ProjectSettingsContext'
import { useUpsertConcreteTestIfiSettingsMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { IFIReportType, notEqual } from '@cims-online/shared'
import { useEffect, useState } from 'react'
import { ConcreteTestFormValues } from './types'
import { useForm } from 'antd/lib/form/Form'
import { toMoment } from 'utils/toMoment'
import ConcreteTestEditPanelCollapse from './components/ConcreteTestEditPanelCollapse'
import { ifiSettingsEditNotification, momentNotEqual } from '../../../../utils'
import CIMSPrompt from 'components/CIMSPrompt'
import { CommonEmbeddedEditPanelProps } from '../../../common'
import { ConcreteTestPanelName } from '../../types'
import EditPanelFooter from '../../../../components/EditPanelFooter'
import ImplicitEnablePanelHeader from '../../../../components/ImplicitEnablePanelHeader'
import { useProjectContext } from 'context/ProjectContext'

const ConcreteTestEditPanel: React.VFC<
  CommonEmbeddedEditPanelProps<ConcreteTestPanelName>
> = ({ dirtyPanels, setDirtyPanels, ...collapseProps }) => {
  const { projectId } = useProjectContext()
  const {
    concreteTestIFISettings: { data, refetch },
  } = useProjectSettingsContext()
  const [upsert, { loading }] = useUpsertConcreteTestIfiSettingsMutation({
    ...DefaultMutationHookOptions,
    onCompleted: () =>
      ifiSettingsEditNotification(IFIReportType.ConcreteTest.type),
  })
  const [initialValues, setInitialValues] = useState<ConcreteTestFormValues>({})

  const handleOnValuesChange: FormProps<ConcreteTestFormValues>['onValuesChange'] =
    (_, currentValues) => {
      const newDirtyPanels: ConcreteTestPanelName[] = []
      if (
        momentNotEqual(initialValues.effectiveDate, currentValues.effectiveDate)
      )
        newDirtyPanels.push('parent')
      if (
        notEqual(
          initialValues.ambientAirEnabled,
          currentValues.ambientAirEnabled
        ) ||
        notEqual(initialValues.minAmbientAir, currentValues.minAmbientAir) ||
        notEqual(
          initialValues.minAmbientAirMessage,
          currentValues.minAmbientAirMessage
        )
      )
        newDirtyPanels.push('ambient')
      if (
        notEqual(
          initialValues.temperatureEnabled,
          currentValues.temperatureEnabled
        ) ||
        notEqual(
          initialValues.temperature?.min,
          currentValues.temperature?.min
        ) ||
        notEqual(
          initialValues.temperature?.max,
          currentValues.temperature?.max
        ) ||
        notEqual(initialValues.lowTempMessage, currentValues.lowTempMessage) ||
        notEqual(initialValues.highTempMessage, currentValues.highTempMessage)
      )
        newDirtyPanels.push('temperature')
      if (
        notEqual(initialValues.slumpEnabled, currentValues.slumpEnabled) ||
        notEqual(
          initialValues.lowSlumpMessage,
          currentValues.lowSlumpMessage
        ) ||
        notEqual(initialValues.highSlumpMessage, currentValues.highSlumpMessage)
      )
        newDirtyPanels.push('slump')
      if (
        notEqual(
          initialValues.percentAirEnabled,
          currentValues.percentAirEnabled
        ) ||
        notEqual(initialValues.lowAirMessage, currentValues.lowAirMessage) ||
        notEqual(initialValues.highAirMessage, currentValues.highAirMessage)
      )
        newDirtyPanels.push('percent')

      setDirtyPanels(newDirtyPanels)
    }

  const [form] = useForm<ConcreteTestFormValues>()

  useEffect(() => {
    form.setFieldsValue(initialValues)
    handleOnValuesChange({}, form.getFieldsValue())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues])

  useEffect(() => {
    setInitialValues({
      effectiveDate: toMoment(data?.effectiveDate),
      ambientAirEnabled: data?.ambientAir?.enabled,
      minAmbientAir: data?.ambientAir?.minimumAmbientAirTemperature,
      minAmbientAirMessage: data?.ambientAir?.message,
      temperatureEnabled: data?.temperature?.enabled,
      temperature: {
        min: data?.temperature?.temperatureLow,
        max: data?.temperature?.temperatureHigh,
      },
      highTempMessage: data?.temperature?.highMessage,
      lowTempMessage: data?.temperature?.lowMessage,
      slumpEnabled: data?.slump?.enabled,
      highSlumpMessage: data?.slump?.highMessage,
      lowSlumpMessage: data?.slump?.lowMessage,
      percentAirEnabled: data?.percentAir?.enabled,
      highAirMessage: data?.percentAir?.highMessage,
      lowAirMessage: data?.percentAir?.lowMessage,
    })
  }, [data])

  return (
    <CIMSPrompt when={dirtyPanels.length > 0}>
      <Form
        form={form}
        onValuesChange={handleOnValuesChange}
        initialValues={initialValues}
        onFinish={async ({
          effectiveDate,
          ambientAirEnabled,
          minAmbientAir,
          minAmbientAirMessage,
          temperatureEnabled,
          temperature,
          highTempMessage,
          lowTempMessage,
          percentAirEnabled,
          highAirMessage,
          lowAirMessage,
          slumpEnabled,
          highSlumpMessage,
          lowSlumpMessage,
        }) => {
          await upsert({
            variables: {
              input: {
                projectId,
                effectiveDate:
                  effectiveDate === null ? null : effectiveDate?.toISOString(),
                ambientAir: {
                  enabled: ambientAirEnabled,
                  minimumAmbientAirTemperature: minAmbientAir,
                  message: minAmbientAirMessage,
                },
                temperature: {
                  enabled: temperatureEnabled,
                  temperatureHigh: temperature?.max,
                  temperatureLow: temperature?.min,
                  highMessage: highTempMessage,
                  lowMessage: lowTempMessage,
                },
                percentAir: {
                  enabled: percentAirEnabled,
                  highMessage: highAirMessage,
                  lowMessage: lowAirMessage,
                },
                slump: {
                  enabled: slumpEnabled,
                  highMessage: highSlumpMessage,
                  lowMessage: lowSlumpMessage,
                },
              },
            },
          })
          refetch()
        }}
      >
        <ImplicitEnablePanelHeader
          reportType={IFIReportType.ConcreteTest.type}
          effectiveDate={initialValues.effectiveDate}
        />
        <ConcreteTestEditPanelCollapse
          form={form}
          dirtyPanels={dirtyPanels}
          {...collapseProps}
        />
        <EditPanelFooter
          {...{
            form,
            loading,
            handleOnValuesChange,
            hasChanges: dirtyPanels.length > 0,
          }}
        />
      </Form>
    </CIMSPrompt>
  )
}

export default ConcreteTestEditPanel
